import { Panels } from '../Consts';
import { useAppContext } from '../../AppContext';
import {
  Group, Header, Panel,
  Caption, Div, SimpleCell,
  IconButton, FormLayoutGroup,
  FormItem, Button, PullToRefresh
} from '@vkontakte/vkui';
import QrCodeComponent from './components/QrCodeComponent';
import React, { useEffect, useRef, useState } from 'react';
import MembersListComponent from './components/MembersListComponent';
import { Icon28Replay } from '@vkontakte/icons';
import NameInputComponent from './components/NameInputComponent';
import { getResponseFromError } from '../../helpers/helpers';
import { clearCache as eventsListCacheClear } from './EventsListPanel';
import { clearCache as eventInfoCacheClear, getEvent } from './ShowEventPanel';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { UploadImageComponent } from './components/UploadImageComponent';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';
import { getRouteEventInfo, getRouteEventsList } from '../../route';
import { StatusSelectComponent } from './components/StatusSelectComponent';

const CURRENT_PANEL = Panels.EVENT_SETTINGS;

const EventSettingsPanel = () => {
  const context = useAppContext();
  const params = context.router.getParams();

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [invite, setInvite] = useState('');
  const [event, setEvent] = useState(null);
  const [members, setMembers] = useState([]);

  const [status, setStatus] = useState('');
  const [statusError, setStatusError] = useState('');

  const [send, setSend] = useState(false);
  const [sendLeave, setSendLeave] = useState(false);
  const [sendUpdateInvite, setSendUpdateInvite] = useState(false);

  const [generateImage, setGenerateImage] = useState(false);//Флаг что аватарка генерируется
  const [sendGenerateImage, setSendGenerateImage] = useState(false);//Флаг что запрос на генерацию аватарки отправлен

  const [error, setError] = useState(null);

  const [isRefreshFetching, setIsRefreshFetching] = useState(false);
  const isFormSavedRef = useRef(false);

  const onSaveHandler = () => {
    if (isFormSavedRef.current) {
      return;
    }

    setSend(true);
    context.spinner(true);
    context.httpClient()
      .updateEventV3(params.eventId, name, status, image?.id)
      .then((response) => {
        eventsListCacheClear(context.cache);
        eventInfoCacheClear(params.eventId, context.cache);
        isFormSavedRef.current = false;
        setSend(false);
        context.spinner(false);

        context.router.backTo(Panels.EVENT_INFO)
          .then(() => {})
          .catch((r) => r.replace(...getRouteEventInfo(params.eventId)));
      })
      .catch((error) => {
        isFormSavedRef.current = false;
        setSend(false);
        context.spinner(false);

        const response = getResponseFromError(error);
        if (!response) {
          context.showError(error);
          return;
        }

        setNameError(response.name || '');
        delete response['name'];

        setImageError(response.image || '');
        delete response['image'];

        if (response.message) {
          context.showError(null, response.message);
        } else if (Object.keys(response).length > 0) {
          context.showError(error);
        }
      });
  }

  const onLeaveHandler = () => {
    context.confirm(
      context.lang().getKey(CURRENT_PANEL, 'leave_event_text'),
      (value) => {
        if (!value || sendLeave) {
          return;
        }

        setSendLeave(true);
        context.httpClient()
          .leaveEvent(params.eventId)
          .then((response) => {
            eventsListCacheClear(context.cache);
            eventInfoCacheClear(params.eventId, context.cache);

            context.router.backTo(Panels.EVENTS_LIST)
              .then(() => {})
              .catch((r) => r.replace(...getRouteEventsList()));
          })
          .catch((error) => {
            context.showError(error);
          })
          .finally(() => {
            context.spinner(false);
            setSendLeave(false);
          });
      },
      context.lang().getKey(CURRENT_PANEL, 'leave_event_go'),
      context.lang().getKey(CURRENT_PANEL, 'leave_event_cancel')
    );
  }

  const onGenerateImage = () => {
    if (!context.store.generateEventImageEnabled) {
      return;
    }

    setSendGenerateImage(true);
    context.spinner(true);
    context.httpClient()
      .generateEventImage(params.eventId)
      .then((response) => {
        setSendGenerateImage(false);
        setGenerateImage(true);
        context.spinner(false);
      })
      .catch((error) => {
        setSendGenerateImage(false);
        context.spinner(false);

        const response = getResponseFromError(error);
        if (!response) {
          context.showError(error);
        } else if (response.message) {
          context.showError(null, response.message);
        } else if (Object.keys(response).length > 0) {
          context.showError(error);
        }
      });
  }

  const fetch = () => {
    context.spinner(true);

    context.init()
      .then(() => {
        getEvent(params.eventId, context)
          .then(({users, credits, event}) => {
            setMembers(Object.values(users));
            setName(event.name);
            setStatus(event.status);
            setEvent(event);
            setImage(event.image);
            setInvite(event.invite_link);
            setGenerateImage(event.is_generate_image);
            setError(null);
            context.spinner(false);
          })
          .catch((e) => {
            setError(e);
            context.showError(e);
          });
      })
      .catch((e) => {
        setError(e);
        context.showError(e);
      });
  }

  const updateInvite = (event) => {
    event.stopPropagation();

    if (sendUpdateInvite) {
      return;
    }

    setSendUpdateInvite(true);
    context.spinner(true);
    context.httpClient()
      .createInvite(params.eventId)
      .then((response) => {
        if (response.data.invite) {
          setInvite(response.data.invite_link);
          setSendUpdateInvite(false);
          eventsListCacheClear(context.cache);
          eventInfoCacheClear(params.eventId, context.cache);
          context.spinner(false);
        }
      })
      .catch((e) => {
        setSendUpdateInvite(false);
        context.spinner(false);
        context.showError(e);
      });
  }

  const onRefresh = () => {
    setIsRefreshFetching(true);

    getEvent(params.eventId, context, true)
      .then(({users, credits, event}) => {
        setMembers(Object.values(users));
        setName(event.name);
        setStatus(event.status);
        setEvent(event);
        setImage(event.image);
        setInvite(event.invite_link);
        setGenerateImage(event.is_generate_image);
        setError(null);
        setIsRefreshFetching(false);
      })
      .catch((e) => {
        setError(e);
        context.showError(e);
        setIsRefreshFetching(false);
      });
  }

  useEffect(() => {
    fetch();
  }, []);

  if (error) {
    return (<ErrorLoadComponent
      error={error}
      onFetch={fetch}
    />)
  }

  if (event === null) {
    return (<Panel nav={CURRENT_PANEL}></Panel>)
  }

  return (<Panel nav={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={context.lang().getKey(CURRENT_PANEL, 'header')}
    />
    {context.snackbar}
    <PullToRefresh onRefresh={() => onRefresh()} isFetching={isRefreshFetching}>
      <Div className={'EventSettings__qrCodeDescription'}>
        <Caption level="2" >
          {context.lang().getKey(CURRENT_PANEL, 'qr_code_description')}
        </Caption>
      </Div>
      <Div className={'EventSettings__qrCode'}>
        <QrCodeComponent text={invite} />
      </Div>

      <form>
        <FormLayoutGroup mode="vertical">
          <Caption level="2" className={'EventSettings__inviteLinkDescription'}>
            {context.lang().getKey(CURRENT_PANEL, 'invite_link_description')}
          </Caption>
          <SimpleCell
            onClick={() => context.getBridge().copyInvite(invite, context, (text) => context.setSnackbar({text}))}
            after={
              <IconButton
                onClick={(event) => updateInvite(event)}
                aria-label={context.lang().getKey(CURRENT_PANEL, 'invite_link_update_button')}
              >
                <Icon28Replay fill={'var(--vkui--color_icon_accent_themed)'} />
              </IconButton>
            }
          >
            {invite}
          </SimpleCell>

          <NameInputComponent
            value={name}
            setValue={setName}
            error={nameError}
            setError={setNameError}
            label={context.lang().getKey('app', 'name_input_label')}
            errorMinLength={context.lang().getKey('errors', 'name_error_min_length_3_char')}
          />
          <StatusSelectComponent
            value={status}
            setValue={setStatus}
            error={statusError}
            setError={setStatusError}
            options={[
              {
                label: context.lang().getKey('app', 'status_label_payments'),
                value: 'payments',
              },
              {
                label: context.lang().getKey('app', 'status_label_repayments'),
                value: 'repayments',
              },
              {
                label: context.lang().getKey('app', 'status_label_all'),
                value: 'all',
              },
              {
                label: context.lang().getKey('app', 'status_label_archived'),
                value: 'archived',
              }
            ]}
            label={context.lang().getKey('app', 'status_input_label')}
          />
          <UploadImageComponent
            value={image}
            setValue={setImage}
            error={imageError}
            setError={setImageError}
            label={context.lang().getKey('app', 'image_input_label')}
            buttonLabel={context.lang().getKey('app', 'image_button_label__empty')}
            maxSizeError={context.lang().getKey('errors', 'image_error_max_size')}
            errorTextType={context.lang().getKey('errors', 'image_error_type')}
            maxSize={context.store.maxFileSize}
          />
          <FormItem>
            <Button
              size="l"
              stretched={true}
              loading={send}
              disabled={send}
              onClick={() => onSaveHandler()}
            >
              {context.lang().getKey(CURRENT_PANEL, 'button_save_label')}
            </Button>
          </FormItem>
        </FormLayoutGroup>
      </form>
      {
        context.store.generateEventImageEnabled && (<FormItem>
          <Button
            size="l"
            stretched={true}
            appearance="neutral"
            loading={sendGenerateImage}
            disabled={generateImage || sendGenerateImage}
            onClick={onGenerateImage}
          >
            {generateImage ? context.lang().getKey(CURRENT_PANEL, 'generating_image') : context.lang().getKey(CURRENT_PANEL, 'button_send_generate_image')}
          </Button>
        </FormItem>)
      }
      <Group
        header={<Header mode="secondary">
          {context.lang().getKey(CURRENT_PANEL, 'users_block_header')}
        </Header>}
      >
        <MembersListComponent members={members} />
        <Div>
          <Button
            size="l"
            stretched={true}
            appearance={'negative'}
            loading={sendLeave}
            disabled={sendLeave}
            onClick={() => onLeaveHandler()}
          >
            {context.lang().getKey(CURRENT_PANEL, 'button_leave_label')}
          </Button>
        </Div>
      </Group>
    </PullToRefresh>
  </Panel>);
}

export default EventSettingsPanel;
